import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid" || data.payment_status === "trialing" || data.payment_status === "active") {
        setStatus("success");
        setPaymentInfo(data);
        
        // Generate deterministic event_id based on Stripe session for deduplication with CAPI
        const eventId = `purchase_${sessionId}`;
        
        // Load pixel SDK if not loaded yet (PaymentSuccess is a marketing page)
        if (!window.fbq) {
          const n = window.fbq = function() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
          if (!window._fbq) window._fbq = n;
          n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
          const t = document.createElement('script'); t.async = true;
          t.src = 'https://connect.facebook.net/en_US/fbevents.js';
          document.head.appendChild(t);
        }
        
        // Fire Purchase event with Advanced Matching
        try {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          const nameParts = (userData.name || '').trim().split(' ');
          const matchData = {};
          if (userData.email) matchData.em = userData.email;
          if (userData.phone) matchData.ph = userData.phone.replace(/[^0-9]/g, '');
          if (nameParts[0]) matchData.fn = nameParts[0].toLowerCase();
          if (nameParts.length > 1) matchData.ln = nameParts.slice(1).join(' ').toLowerCase();
          window.fbq('init', '2770682049943810', matchData);
        } catch (e) {
          window.fbq('init', '2770682049943810');
        }
        
        window.fbq('trackSingle', '2770682049943810', 'Purchase', {
          value: (data.amount || 0) / 100,
          currency: data.currency?.toUpperCase() || 'MXN',
          content_name: data.plan_id || 'subscription',
          content_type: 'subscription',
          num_items: 1
        }, { eventID: eventId });
        console.log('Meta Pixel: Purchase event fired with eventID:', eventId);
        
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-16 h-16 text-brand-purple mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold mb-2">Procesando pago...</h2>
            <p className="text-gray-600">
              Por favor espera mientras verificamos tu pago.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error en el pago</h2>
            <p className="text-gray-600 mb-6">
              No pudimos verificar tu pago. Si crees que esto es un error, 
              por favor contacta a soporte.
            </p>
            <div className="space-y-3">
              <Link to="/precios">
                <Button className="w-full bg-brand-purple hover:bg-brand-pink">
                  Intentar de nuevo
                </Button>
              </Link>
              <Link to="/admin/dashboard">
                <Button variant="outline" className="w-full">
                  Ir al Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <CardTitle className="text-2xl">¡Pago Exitoso!</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-gray-600 mb-6">
            Tu suscripción ha sido activada correctamente. 
            <strong> Ya puedes comenzar a crear tus landing pages.</strong>
          </p>
          
          {paymentInfo && (
            <div className="bg-purple-50 rounded-lg p-4 mb-6 text-left border border-purple-200">
              <p className="text-sm text-purple-700 font-medium mb-2">Detalles de tu suscripción:</p>
              <p className="text-lg font-bold text-purple-900">
                ${((paymentInfo.amount || 0) / 100).toLocaleString()} {paymentInfo.currency?.toUpperCase()}/mes
              </p>
              <p className="text-sm text-purple-600">
                Renovación automática cada mes
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Link to="/admin/dashboard">
              <Button className="w-full bg-brand-purple hover:bg-brand-pink">
                <Home className="w-4 h-4 mr-2" />
                Ir a Mi Panel
              </Button>
            </Link>
            <Link to="/admin/properties/new">
              <Button variant="outline" className="w-full">
                Crear Mi Primera Propiedad
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
